import CancelPlanIcon from "@/assets/colorable/cancel_plan.svg?react";
import InfoIcon from "@/assets/colorable/info.svg?react";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import TickIcon from "@/assets/colorable/tick.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import UpgradeIcon from "@/assets/colorable/upgrade.svg?react";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import AiChatIcon from "@/assets/dark_gradient/ai_chat.svg?react";
import CampaignInfinityIcon from "@/assets/dark_gradient/campaign_infinity.svg?react";
import CircleWithClockIcon from "@/assets/dark_gradient/circle_with_clock.svg?react";
import CircleWithHalfClockIcon from "@/assets/dark_gradient/circle_with_half_clock.svg?react";
import InfluencerNetworkAccessIcon from "@/assets/dark_gradient/influencer_network_access.svg?react";
import SearchIcon from "@/assets/dark_gradient/search.svg?react";
import SocialAdsIcon from "@/assets/dark_gradient/social_ads.svg?react";
import UgcIcon from "@/assets/dark_gradient/ugc.svg?react";
import UserChartsIcon from "@/assets/dark_gradient/user_charts.svg?react";
import Button from "@/components/Common/Button";
import CheckBox from "@/components/Common/CheckBox";
import Loader from "@/components/Common/kpi/Loader";
import TextButton from "@/components/Common/TextButton";
import usePackages, {getPackageIcon} from "@/hooks/usePackages";
import {usePlans} from "@/hooks/usePlans";
import {usePriceFormat} from "@/hooks/usePriceFormat";
import {useAgencyId, useIsAgency, useSelectedCompany} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import InactiveAgencyPlan from "@/pages/plans/InactiveAgencyPlan";
import PlanAddOns from "@/pages/plans/PlanAddOns";
import PlanPerks from "@/pages/plans/PlanPerks";
import PlansAdminAssigned from "@/pages/plans/PlansAdminAssigned";
import PlanRecommendation from "@/pages/plans/recommendation/PlanRecommendation";
import RecommendationChip from "@/pages/plans/recommendation/RecommendationChip";
import UpgradePlan from "@/pages/plans/UpgradePlan";
import {Can} from "@/permissions";
import {UGC_COST_PER_VIDEO_15_SECONDS, UGC_COST_PER_VIDEO_30_SECONDS, useMiscActions} from "@/store/campaignForm";
import {VAT_PERCENTAGE} from "@/store/constants";
import {recommendedPlansAtom, scrollSectionAtom, settingsModalAtom} from "@/store/jotaiAtoms";
import {usePaymentStore} from "@/store/payment";
import type {TCurrency, TPackageName, TSubscriptionWithDiscountOptions} from "@/store/types";
import useUserStore from "@/store/user";
import {getRecommendationLevel} from "@/utils/planUtils";
import {createFileRoute, useRouter} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import {useAtom, useSetAtom} from "jotai";
import {useEffect, useRef, useState, type ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {useInView} from "react-intersection-observer";
import {toast} from "react-toastify";
import {useTitle, useUnmount, useWindowSize} from "react-use";
import {z} from "zod";

export const Route = createFileRoute("/_authenticated/plans")({
  component: Plans,
  validateSearch: zodValidator(
    z.object({
      campaign_id: z.number().optional(),
      campaign_size: z.enum(["5-10", "10-15", "20-50", "50-100", "100+"]).optional(),
      pro: z.boolean().optional(),
      redirect_reason: z.string().optional(),
      type: z.enum(["instagram", "instagram_and_tiktok", "tiktok"]).optional(),
    }),
  ),
});

function Plans() {
  const {t} = useTranslation(["common", "plans"]);
  const navigate = Route.useNavigate();
  const router = useRouter();
  const {
    campaign_size: campaignSize,
    pro: isProModeOn,
    redirect_reason: redirectReason,
    type: campaignCreationType,
  } = Route.useSearch();
  useTitle(t("ourPlans"));
  const [scrollSection, setScrollSection] = useAtom(scrollSectionAtom);
  const setSettingsModal = useSetAtom(settingsModalAtom);
  const campaignActions = useMiscActions();
  const isAgency = useIsAgency();
  const agencyId = useAgencyId();
  const user = useUserStore(state => state.user);
  const advancedOptionsRef = useRef<HTMLDivElement>(null);
  const vatRef = useRef<HTMLSpanElement>(null);
  const serviceFeeRef = useRef<HTMLSpanElement>(null);

  const setPaymentValue = usePaymentStore(state => state.setter);
  const showTwoWeeksTrial = useUserStore(
    state => !state.user.package_subscription && !state.user.has_past_package_subscription,
  );
  const subscription = useUserStore(state => state.user.package_subscription);

  const [recommendedPlans, setRecommendedPlans] = useAtom(recommendedPlansAtom);
  const isInstagramSelected = usePaymentStore(state => state.isInstagramSelected);
  const isTiktokSelected = usePaymentStore(state => state.isTiktokSelected);
  const selectedPaymentPeriod = usePaymentStore(state => state.selectedPeriod);
  const selectedPlanCategory = usePaymentStore(state => state.getPlanCategory());
  const getCost = usePaymentStore(state => state.getCost);
  const getIsCurrentPlan = usePaymentStore(state => state.getIsCurrentPlan);
  const isUpgradePlanModalOpen = usePaymentStore(state => state.isUpgradePlanModalOpen);
  const {handlePlanClick} = usePlans();
  const formatPrice = usePriceFormat();
  const selectedCompany = useSelectedCompany();
  const accountName = selectedCompany?.company_name ?? user.agencies?.at(0)?.agency?.name;
  const hubspotIframeRef = useRef<HTMLIFrameElement>(null);
  const hasLoadedUser = useUserStore(state => !!state.user.id);
  const currency = useUserStore(state => state.user.package_subscription?.currency ?? state.user.currency);
  const hasEssentialsPlan = usePaymentStore(state => state.hasEssentialsPlan());
  const [isRecommendationVisible, setRecommendationVisible] = useState(false);
  const {height} = useWindowSize();
  const isNarrowScreen = height < 890;

  const renderCancelPlanButton = (isCurrentPlan?: boolean) => {
    if (
      !isCurrentPlan ||
      (subscription?.status !== "active" && subscription?.status !== "trialing") ||
      !!subscription?.cancellation_date
    ) {
      return null;
    }

    return (
      <button
        className="text-primary-dark-solid hover:text-primary-medium-solid flex w-fit items-center gap-2 self-center text-sm font-medium transition-all duration-150"
        onClick={() => {
          if (isAgency && agencyId) {
            setSettingsModal({scope: {id: agencyId, type: "agency"}, section: "billing"});
          } else if (selectedCompany?.id) {
            setSettingsModal({scope: {id: selectedCompany.id, type: "brand"}, section: "billing"});
          }
        }}>
        <CancelPlanIcon className="size-4 shrink-0" />
        {t("cancelPlan")}
        <RightArrowIcon className="size-3 shrink-0" />
      </button>
    );
  };

  const isTikTokDisabled =
    !isInstagramSelected || subscription?.package_category === "TIKTOK" || subscription?.package_category === "BOTH";
  const {
    packagesList,
    extendedData: extendedPlansData,
    isPending,
    packages: plansObject,
  } = usePackages(currency as TCurrency);

  const getExtendedData = (): TSubscriptionWithDiscountOptions[] => {
    if (!hasEssentialsPlan || !subscription) return extendedPlansData;
    const plansWithoutStarter = extendedPlansData.filter(plan => plan.name !== "STARTER");
    return [
      {
        account_setup_call: false,
        customer_success: false,
        ...subscription,
        package_category:
          subscription.instagram_campaigns && subscription.tiktok_campaigns
            ? "BOTH"
            : subscription.tiktok_campaigns
              ? "TIKTOK"
              : "INSTAGRAM",
        price_per_month_with_discount: subscription.price_per_month!,
        price_per_year_with_discount: subscription.price_per_year!,
        discount_percentage: 0,
        recurring: false,
      },
      ...plansWithoutStarter,
    ];
  };

  const isInstagramDisabled = !isTiktokSelected || subscription?.instagram_campaigns;
  const showContactSupportToChange = !recommendedPlans && !!subscription;

  const isSwissBrand = currency === "CHF";

  const plans = packagesList
    .filter(plan => {
      if (hasEssentialsPlan) {
        return plan.type !== "STARTER";
      } else {
        return plan.type !== "ESSENTIALS";
      }
    })
    .filter(p => p.type !== "STARTER")!;

  const {ref: planRecommendationContainerRef} = useInView({
    threshold: 0.4,
    onChange: (_, entry) => {
      setRecommendationVisible(entry.isIntersecting);
    },
  });

  const getPlanInfo = (
    plan: TSubscriptionWithDiscountOptions,
  ): {isCurrentPlan?: boolean; label: string; prefixIcon?: ReactNode; suffixIcon?: ReactNode} => {
    if (recommendedPlans) {
      const isPlanUnsupported =
        getRecommendationLevel({
          packageType: plan?.name ?? "ADVANCED",
          recommendedPackageType: recommendedPlans?.recommended?.name ?? "ADVANCED",
        }) === "unsupported";

      return {
        suffixIcon: (
          <UpgradeIcon
            className={clsxm("size-4 shrink-0 text-white", isPlanUnsupported && "text-neutral-grey-light")}
          />
        ),
        label:
          plan.name === "ENTERPRISE"
            ? t("contactOurSalesTeam")
            : t("plans:publish_with_plan", {
                plan: t(plansObject[plan.name ?? "ADVANCED"].translationKey as "packageAdvanced"),
              }),
      };
    }

    if (!subscription) {
      return {
        suffixIcon: <RightArrowIcon className="size-3.5 shrink-0 text-white" />,
        label: t(plan.name === "ENTERPRISE" ? "contactOurSalesTeam" : "tryItNowRiskFree"),
      };
    }
    if (getIsCurrentPlan(plan)) {
      return {
        isCurrentPlan: true,
        label: t("accounts_current_package"),
        prefixIcon: <TickIcon className="text-success-herbal size-4" />,
      };
    }

    if (plan.name === "ENTERPRISE") {
      return {
        label: t("contactOurSalesTeam"),
        suffixIcon: <RightArrowIcon className="size-3.5 shrink-0 text-white" />,
      };
    }

    const otherPlan = getExtendedData().find(extendedPlan => extendedPlan.name === plan.name)!;

    return {
      label:
        subscription?.price_per_month! < otherPlan?.price_per_month! &&
        subscription.package_type === selectedPaymentPeriod &&
        (subscription.package_category === otherPlan.package_category || selectedPlanCategory === "BOTH") &&
        !(subscription.name === "PROFESSIONAL" && otherPlan.name === "ADVANCED")
          ? t("upgrade_now")
          : subscription?.price_per_year! <= otherPlan?.price_per_year! &&
              selectedPaymentPeriod === "YEARLY" &&
              (subscription.package_category === otherPlan.package_category || selectedPlanCategory === "BOTH") &&
              !(subscription.name === "PROFESSIONAL" && otherPlan.name === "ADVANCED")
            ? t("upgradeAndChangeToYearlyBilling")
            : "",
      suffixIcon: <UpgradeIcon className="size-5 shrink-0 text-white" />,
    };
  };

  const scrollToVat = () => {
    vatRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };
  const scrollToServiceFee = () => {
    serviceFeeRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const enterprisePlanInfo =
    getExtendedData().length === 0 ? undefined : getPlanInfo(getExtendedData()[getExtendedData().length - 1]);

  const isEssentialSettingMissing =
    (campaignCreationType === "instagram" && !isInstagramSelected) ||
    (campaignCreationType === "tiktok" && !isTiktokSelected) ||
    (campaignCreationType === "instagram_and_tiktok" && (!isInstagramSelected || !isTiktokSelected));

  useEffect(() => {
    const plansInOrder: TPackageName[] = ["ADVANCED", "PROFESSIONAL", "EXPERT"];

    if (!isPending && !recommendedPlans && campaignSize) {
      const getPlan = (name: TPackageName) => extendedPlansData.find(plan => plan.name === name);

      const getPlanIndexByProMode = (index: number): number => {
        if (index >= plansInOrder.length) {
          return plansInOrder.length - 1;
        }

        if (!isProModeOn) {
          return index;
        }

        const recommendedPlan = getPlan(plansInOrder[index]);

        return recommendedPlan?.pro_creator_mode ? index : getPlanIndexByProMode(index + 1);
      };

      const recommendedPlanIndexByCampaignSize = campaignSize === "5-10" ? 0 : campaignSize === "10-15" ? 1 : 2;

      const recommendedPlanIndex = getPlanIndexByProMode(recommendedPlanIndexByCampaignSize);
      const recommendedPlan = getPlan(plansInOrder[recommendedPlanIndex]);

      setRecommendedPlans({
        power_user:
          recommendedPlanIndex + 1 <= plansInOrder.length - 1
            ? getPlan(plansInOrder[recommendedPlanIndex + 1])
            : undefined,
        recommended: recommendedPlan,
      });
    }
  }, [campaignSize, extendedPlansData, isPending, isProModeOn, recommendedPlans]);

  useEffect(() => {
    if (subscription) {
      setPaymentValue(state => {
        state.selectedPeriod = subscription?.package_type! || "MONTHLY";
        state.isInstagramSelected = subscription?.instagram_campaigns!;
        state.isTiktokSelected = subscription?.tiktok_campaigns!;
      });
    }

    if (campaignCreationType === "instagram") {
      setPaymentValue(state => {
        state.isInstagramSelected = true;
      });
    }

    if (campaignCreationType === "tiktok") {
      setPaymentValue(state => {
        state.isInstagramSelected = subscription?.instagram_campaigns!;
        state.isTiktokSelected = true;
      });
    }

    if (campaignCreationType === "instagram_and_tiktok") {
      setPaymentValue(state => {
        state.isInstagramSelected = true;
        state.isTiktokSelected = true;
      });
    }
  }, [subscription, campaignCreationType]);

  useEffect(() => {
    if (!isPending && hasLoadedUser && scrollSection === "hubspot")
      setTimeout(
        () =>
          hubspotIframeRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          }),
        0,
      );
  }, [isPending, hasLoadedUser, scrollSection]);

  useUnmount(() => {
    setScrollSection(null);
  });

  useUnmount(() => {
    setRecommendedPlans(undefined);
  });

  useEffect(() => {
    if (redirectReason === "already_logged_in") {
      toast.info(
        <div className="flex items-center gap-3">
          <InfoIcon className="text-primary-dark-solid size-5 shrink-0" />
          <span className="text-neutral-black font-medium">{t("plans:already_logged_in_redirect")}</span>
        </div>,
        {
          className:
            "mx-auto mb-1 max-w-[90vw] w-fit rounded-md border border-neutral-white-dark bg-white px-4 py-2 shadow-lg md:max-w-[auto]",
        },
      );
    }
  }, [redirectReason]);

  if (isPending || !hasLoadedUser) {
    return (
      <Loader
        styles={{
          containerClassName: "h-[90vh] w-full items-center",
          loaderClassName: "size-20",
        }}
      />
    );
  }

  if (subscription?.is_admin_assigned) {
    return <PlansAdminAssigned />;
  }

  if (isAgency && (!subscription || subscription.payment_status === "PAYMENT_FAILED")) {
    return <InactiveAgencyPlan />;
  }

  const includedInEveryPlanPerks: {icon: ReactNode; label: string; suffix?: ReactNode}[] = [
    {
      icon: <CampaignInfinityIcon className="size-7 shrink-0 drop-shadow-none" />,
      label: t("packagePerkUnlimitedCampaigns"),
    },
    {
      icon: <InfluencerNetworkAccessIcon className="size-7 shrink-0 drop-shadow-none" />,
      label: t("packagePerkAccessToInfluencerNetwork"),
    },
    {
      icon: <UserChartsIcon className="size-7 shrink-0 drop-shadow-none" />,
      label: t("packagePerkInsights"),
    },
    {
      icon: <AiChatIcon className="size-7 shrink-0 drop-shadow-none" />,
      label: t("packagePerkAiChatSupport"),
    },
    {
      icon: <SocialAdsIcon className="h-7 w-14 shrink-0 drop-shadow-none" />,
      label: t("packagePerkAdIntegration"),
    },
    {
      icon: <SearchIcon className="size-7 shrink-0 drop-shadow-none" />,
      label: t("packagePerkViewPastContent"),
      suffix: (
        <span className="text-neutral-grey-dark cursor-pointer" onClick={scrollToServiceFee}>
          **
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col">
      <div className="bg-linear-(--plans-background-gradient) bg-size-[100%_1500px] bg-fixed">
        {isUpgradePlanModalOpen && <UpgradePlan isSwissBrand={isSwissBrand} />}
        <div className="max-w-wide container mx-auto flex flex-col items-stretch justify-center px-8">
          <Button
            buttonStyle="White"
            className="shadow-elevation-sm sticky top-16 z-999 ml-2 min-h-15 w-fit px-8 py-4.5"
            onClick={() => (window.history.length > 2 ? router.history.back() : navigate({to: "/"}))}>
            <div className="flex items-center justify-center gap-2.5">
              <RightArrowIcon className="text-primary-dark-solid size-3.5 rotate-180" />
              {t("modalBack")}
            </div>
          </Button>
          <div className={clsxm("mt-5 flex flex-col pb-10 lg:-mt-16", !recommendedPlans && "gap-10")}>
            {recommendedPlans ? (
              <div ref={planRecommendationContainerRef} className="flex lg:min-h-[calc(100vh-140px)]">
                <PlanRecommendation />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 self-center pt-8 sm:flex-row">
                <div className="flex flex-col items-center gap-5">
                  <span className="font-domaine-display text-primary-midnight text-center text-4xl font-bold md:text-5xl">
                    {t(!subscription ? "joinNowRiskFree" : "upgradeYourPlan")}
                  </span>
                  {!subscription && (
                    <span className="text-neutral-balck text-center text-lg font-semibold md:text-xl">
                      {t("twoWeekCancelAnytime")}
                    </span>
                  )}
                </div>
                <div />
              </div>
            )}
            <div className={clsxm(recommendedPlans && "mt-10 flex flex-col items-center gap-6 lg:mt-0")}>
              {recommendedPlans && (
                <div className="flex w-full justify-end sm:justify-center">
                  <button
                    className="text-primary-dark-solid flex gap-3 sm:items-center"
                    onClick={() => {
                      if (isRecommendationVisible) {
                        advancedOptionsRef?.current?.scrollIntoView({
                          behavior: "instant",
                          block: "start",
                        });
                      } else {
                        window.scrollTo({top: 0, behavior: "instant"});
                      }
                    }}>
                    <span className="font-semibold">
                      {t(isRecommendationVisible ? "advanced_options" : "recommended")}
                    </span>
                    <RightChevronIcon
                      className={clsxm("w-2 rotate-90 transition-all", !isRecommendationVisible && "-rotate-90")}
                    />
                  </button>
                </div>
              )}
              <div
                ref={advancedOptionsRef}
                className="rounded-2.5xl flex scroll-mt-35 flex-col gap-4 bg-white p-5 xl:gap-11">
                {recommendedPlans && isEssentialSettingMissing && (
                  <div className="border-neutral-grey-light-dark bg-neutral-grey-lightest flex min-h-11.5 w-fit items-center gap-3 self-center rounded-sm border p-3">
                    <WarningIcon className="text-warning-medium-yellow size-4" />
                    <span className="text-neutral-black">{t("plans:missing_essential_setting")}</span>
                  </div>
                )}
                <div className="flex w-full flex-col items-center justify-center gap-4 self-center lg:flex-row xl:justify-evenly xl:gap-12">
                  <div className="flex flex-col gap-2">
                    <div className="text-neutral-black text-center">
                      <span className="font-semibold">{t("step", {count: 1})}:</span> {t("plansStepOne")}
                    </div>
                    <div className="flex gap-2 self-center sm:gap-5">
                      <div
                        data-testid="plans-instagram-option"
                        onClick={() => {
                          if (!isInstagramDisabled)
                            setPaymentValue(state => {
                              state.isInstagramSelected = !state.isInstagramSelected;
                            });
                        }}
                        className={clsxm(
                          "border-neutral-grey-light flex min-h-20 w-36 flex-col items-center justify-center gap-2 rounded-md border px-3 py-2.5 md:w-44",
                          isInstagramSelected ? "bg-primary-light-solid" : "bg-white",
                          isInstagramDisabled ? "cursor-auto" : "cursor-pointer",
                        )}>
                        {showContactSupportToChange && (
                          <Can I="create" a="MailToSupport">
                            <span onClick={event => event.stopPropagation()}>
                              <TextButton
                                className="enabled:text-neutral-grey-light enabled:hover:text-neutral-grey-light/70 text-xs font-semibold underline"
                                text={t("plans:contact_support_to_change")}
                                onClick={() =>
                                  window.open(
                                    `mailto:invoice@refluenced.com?subject=${encodeURIComponent(
                                      [accountName, t("plans:remove_social_access_subject")].filter(Boolean).join(": "),
                                    )}`,
                                  )
                                }
                              />
                            </span>
                          </Can>
                        )}
                        <div className="flex items-center gap-2">
                          <CheckBox
                            isChecked={isInstagramSelected}
                            disabled={isInstagramDisabled}
                            className="border-bold-blue-100"
                          />
                          <div className="text-neutral-black flex items-center gap-2 font-medium">
                            <InstagramIcon className="size-4" />
                            {t("instagram")}
                          </div>
                        </div>
                        {showContactSupportToChange && <div className="grow" />}
                      </div>
                      <div className="rounded-lg bg-linear-(--primary-light-gradient) p-px">
                        <div
                          data-testid="plans-tiktok-option"
                          onClick={() => {
                            if (!isTikTokDisabled)
                              setPaymentValue(state => {
                                state.isTiktokSelected = !state.isTiktokSelected;
                              });
                          }}
                          className={clsxm(
                            "border-neutral-grey-light flex h-full min-h-20 w-36 items-center justify-center gap-2 rounded-md border px-3 py-2.5 md:w-44",
                            isTiktokSelected ? "bg-primary-light-solid" : "bg-white",
                            isTikTokDisabled ? "cursor-auto" : "cursor-pointer",
                          )}>
                          <div className="flex w-full items-center justify-center gap-2">
                            <CheckBox
                              isChecked={isTiktokSelected}
                              disabled={isTikTokDisabled}
                              className="border-bold-blue-100"
                            />
                            <div className="text-neutral-black flex items-center gap-2 font-medium">
                              <TiktokIcon className="size-4" />
                              {t("tiktok")}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 self-stretch">
                    <div className="text-neutral-black text-center">
                      <span className="font-semibold">{t("step", {count: 2})}:</span> {t("plansStepTwo")}
                    </div>
                    <div className="flex w-72 self-center md:w-88">
                      <div className="flex-1" />
                      <div className="flex flex-1 justify-center">
                        <div
                          className={clsxm(
                            "rounded-full p-px",
                            selectedPaymentPeriod === "YEARLY"
                              ? "bg-linear-(--brand-medium-gradient)"
                              : "bg-primary-light-solid",
                          )}>
                          <div className="h-full w-full rounded-full bg-white px-3 py-2 text-xs font-semibold">
                            <span
                              className={clsxm(
                                selectedPaymentPeriod === "YEARLY"
                                  ? "bg-linear-(--primary-dark-gradient) bg-clip-text text-transparent"
                                  : "text-neutral-grey-light",
                              )}>
                              {t("plansYearlyDiscount")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-1 justify-center">
                      <button
                        data-testid="plans-period-monthly"
                        onClick={() =>
                          setPaymentValue(state => {
                            state.selectedPeriod = "MONTHLY";
                          })
                        }
                        className={
                          "min-h-12 w-36 rounded-l-md py-2.5 text-sm font-medium transition-all md:w-44" +
                          (selectedPaymentPeriod === "MONTHLY"
                            ? " bg-primary-dark-solid text-white"
                            : " border bg-white")
                        }>
                        <span
                          className={selectedPaymentPeriod === "MONTHLY" ? "text-white" : "text-neutral-grey-light"}>
                          {t("perMonthShort")}
                        </span>
                      </button>
                      <button
                        data-testid="plans-period-yearly"
                        onClick={() =>
                          setPaymentValue(state => {
                            state.selectedPeriod = "YEARLY";
                          })
                        }
                        className={
                          "relative min-h-12 w-36 rounded-r-md py-2.5 text-sm font-medium transition-all md:w-44" +
                          (selectedPaymentPeriod === "YEARLY"
                            ? " bg-primary-dark-solid text-white"
                            : " text-neutral-grey-light border bg-white")
                        }>
                        {t("perYearShort")}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="text-neutral-black text-center">
                  <span className="font-semibold">{t("step", {count: 3})}:</span> {t("plansStepThree")}
                </div>
                <div className="flex w-full flex-wrap justify-center gap-5 gap-y-10">
                  {plans.slice(0, -1).map((plan, index) => {
                    const planData = getExtendedData().find(p => p.name === plan.type);
                    if (!planData) return;

                    const planInfo = getPlanInfo(planData);
                    const isPlanUnsupported =
                      getRecommendationLevel({
                        packageType: planData.name as TPackageName,
                        recommendedPackageType: recommendedPlans?.recommended?.name ?? "ADVANCED",
                      }) === "unsupported";
                    const upgradeButtonStyle = planInfo.isCurrentPlan
                      ? "Custom"
                      : planData.name === "EXPERT"
                        ? "Gradient"
                        : "White";
                    const isWhiteUpgradeButton = upgradeButtonStyle === "White";

                    return (
                      <div
                        key={index}
                        data-testid={`plans-card-${plan.type.toLowerCase()}`}
                        className={
                          "relative flex min-w-87.5 flex-1 flex-col justify-between rounded-lg border bg-white p-5 pt-10 drop-shadow-md transition-all duration-150 " +
                          (planInfo.isCurrentPlan ? "border-primary-medium-solid" : " border-neutral-white-dark")
                        }>
                        {recommendedPlans ? (
                          <div className="absolute top-0 right-0 left-0 mx-auto w-min -translate-y-1/2 whitespace-nowrap">
                            <RecommendationChip isSmall={isNarrowScreen} packageType={plan.type} />
                          </div>
                        ) : (
                          plan.type === "EXPERT" && (
                            <div className="absolute -top-6 right-0 left-0 mx-auto w-min rounded-full border-2 border-white bg-linear-(--primary-dark-gradient) px-4 py-2 text-lg whitespace-nowrap text-white shadow-md">
                              {t("mostPopularPlan")}
                            </div>
                          )
                        )}
                        <div className="flex flex-col gap-5">
                          <div className="flex flex-col items-center gap-2">
                            {getPackageIcon({packageType: plan.type, className: "size-10 shrink-0"})}
                            <span className="text-neutral-grey-dark text-2xl font-semibold capitalize">
                              {plan.name}
                            </span>
                          </div>
                          <div className="-my-2 flex items-center justify-center gap-2">
                            {isInstagramSelected && <InstagramIcon className="size-4" />}
                            {isInstagramSelected && isTiktokSelected && (
                              <div className="bg-neutral-black size-1 rounded-full" />
                            )}
                            {isTiktokSelected && <TiktokIcon className="size-4" />}
                          </div>
                          <div className="flex items-baseline justify-center">
                            <span className="text-neutral-black text-4xl font-bold md:text-5xl">
                              {getCost(formatPrice, planData)}
                            </span>
                            <div className="flex flex-col">
                              <span className="relative bg-linear-(--primary-dark-gradient) bg-clip-text pl-1 text-2xl text-transparent">
                                {t("costPerMonth")}
                                <span
                                  className="text-neutral-grey-dark absolute -top-3.5 -left-0.5 cursor-pointer text-2xl -tracking-wide md:-top-5.5"
                                  onClick={scrollToVat}>
                                  *
                                </span>
                              </span>
                              {selectedPaymentPeriod === "YEARLY" && (
                                <span className="text-neutral-black pl-1 text-sm">{t("package_perk_yearly")}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex w-full flex-col items-start gap-3">
                            {showTwoWeeksTrial && (
                              <div className="bg-primary-light-solid text-primary-dark-solid w-full rounded-lg border-3 border-white py-1.5 text-center text-xl font-semibold shadow-xs drop-shadow-xs">
                                {t("twoWeeksTrial")}
                              </div>
                            )}
                            <span className="text-neutral-black pt-2 text-sm">{t("whatIsIncluded")}</span>
                            <PlanPerks plan={planData} />
                          </div>
                        </div>
                        {!!planInfo.label && (
                          <Can I="update" a="CompanyPlan" passThrough>
                            {allowed =>
                              (planInfo.isCurrentPlan || allowed) && (
                                <div className="mt-10 flex w-full flex-col gap-5">
                                  {renderCancelPlanButton(planInfo.isCurrentPlan)}
                                  <Button
                                    buttonStyle={upgradeButtonStyle}
                                    dataTestId={`plans-buy-button-${planData.name?.toLowerCase()}`}
                                    isDisabled={isPlanUnsupported}
                                    onClick={handlePlanClick({plan: planData})}
                                    className={clsxm(
                                      "disabled:bg-neutral-white-dark w-full gap-2",
                                      planInfo.isCurrentPlan &&
                                        "bg-neutral-grey-lightest disabled:bg-neutral-grey-lightest",
                                      isPlanUnsupported && "text-neutral-grey-light",
                                      isWhiteUpgradeButton && !isPlanUnsupported && "[&_svg]:text-primary-dark-solid",
                                    )}>
                                    {planInfo.prefixIcon}
                                    <span
                                      data-testid={planInfo.isCurrentPlan ? "plans-current-plan-label" : undefined}
                                      className={clsxm(
                                        "font-semibold",
                                        planInfo.isCurrentPlan
                                          ? "text-success-herbal"
                                          : isWhiteUpgradeButton
                                            ? "text-primary-dark-solid"
                                            : "text-white",
                                        isPlanUnsupported && "text-neutral-grey-light",
                                      )}>
                                      {planInfo.label}
                                    </span>
                                    {planInfo.suffixIcon}
                                  </Button>
                                </div>
                              )
                            }
                          </Can>
                        )}
                      </div>
                    );
                  })}
                </div>
                <PlanAddOns />
                <div className="flex flex-col items-center gap-5">
                  <span className="text-neutral-black text-center text-2xl font-semibold md:text-4xl">
                    {t("includedInEveryPlan")}
                  </span>
                  <div className="border-neutral-white-dark grid w-full grid-cols-1 gap-4 gap-y-6 rounded-md md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
                    {includedInEveryPlanPerks.map((perk, i) => (
                      <div
                        key={i}
                        className="border-neutral-white-dark flex flex-col justify-center gap-2 rounded-3xl border bg-white p-3 drop-shadow-lg xl:p-5">
                        {perk.icon}
                        <span className={clsxm("font-neutral-black text-sm xl:text-base", perk.suffix && "relative")}>
                          {perk.label}
                          {perk.suffix}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex w-full flex-col gap-5">
                  <span className="text-neutral-black text-center text-2xl font-semibold md:text-4xl">
                    {t("alternativesForBetterFit")}
                  </span>
                  <div className="flex w-full flex-wrap justify-center gap-10 gap-y-10">
                    <>
                      <div
                        className={
                          "relative flex max-w-150 min-w-87.5 flex-1 flex-col justify-between rounded-lg border bg-white p-5 pt-10 drop-shadow-md transition-all duration-150 " +
                          (enterprisePlanInfo?.isCurrentPlan
                            ? "border-primary-medium-solid"
                            : " border-neutral-white-dark")
                        }>
                        <div className="flex flex-col gap-5">
                          <div className="flex flex-col items-center gap-2">
                            {getPackageIcon({
                              packageType: plansObject.ENTERPRISE.type,
                              className:
                                "size-10 overflow-visible rounded-full border border-neutral-white-dark bg-white p-2 drop-shadow-xs",
                            })}
                            <span className="text-neutral-grey-dark text-2xl font-semibold capitalize">
                              {t(plansObject.ENTERPRISE.translationKey as "packageTailored")}
                            </span>
                          </div>
                          <div className="flex flex-col items-center justify-center gap-5">
                            <span className="text-neutral-black text-center text-4xl font-bold md:text-5xl">
                              {t("letsTalk")}
                            </span>
                            {showTwoWeeksTrial && (
                              <div className="bg-primary-light-solid text-primary-dark-solid w-full rounded-lg border-3 border-white py-1.5 text-center text-xl font-semibold shadow-xs drop-shadow-xs">
                                {t("twoWeeksTrial")}
                              </div>
                            )}
                            <span className="text-neutral-black text-left text-sm">
                              {t("enterprisePlanDisclaimer")}
                            </span>
                            <span className="text-neutral-black font-semibold">{t("salesExcitedToTalk")}</span>
                          </div>
                        </div>
                        {!!enterprisePlanInfo?.label && (
                          <Can I="update" a="CompanyPlan" passThrough>
                            {allowed =>
                              (enterprisePlanInfo.isCurrentPlan || allowed) && (
                                <div className="mt-10 flex w-full flex-col gap-5">
                                  {renderCancelPlanButton(enterprisePlanInfo.isCurrentPlan)}
                                  <Button
                                    buttonStyle={enterprisePlanInfo.isCurrentPlan ? "Custom" : "Blue"}
                                    onClick={handlePlanClick({plan: getExtendedData()[getExtendedData().length - 1]})}
                                    className={clsxm(
                                      "w-full gap-2",
                                      enterprisePlanInfo.isCurrentPlan &&
                                        "bg-neutral-grey-lightest disabled:bg-neutral-grey-lightest",
                                    )}>
                                    {enterprisePlanInfo.prefixIcon}
                                    <span
                                      className={clsxm(
                                        "font-semibold",
                                        enterprisePlanInfo.isCurrentPlan ? "text-success-herbal" : "text-white",
                                      )}>
                                      {enterprisePlanInfo.label}
                                    </span>
                                    {enterprisePlanInfo.suffixIcon}
                                  </Button>
                                </div>
                              )
                            }
                          </Can>
                        )}
                      </div>
                      {!!subscription && (
                        <div className="border-neutral-white-dark relative flex max-w-150 min-w-87.5 flex-1 flex-col justify-between rounded-lg border bg-white p-5 pt-10 drop-shadow-md">
                          <div className="bg-primary-light-solid absolute inset-x-0 -top-6 mx-auto w-min rounded-full border-2 border-white px-5 py-2 drop-shadow-md">
                            <span className="text-kg text-primary-dark-solid font-semibold whitespace-nowrap">
                              {t("billedSeparatelyFromPlans")}
                            </span>
                          </div>
                          <div className="flex h-full flex-col justify-between gap-5">
                            <div className="flex flex-col items-center gap-2">
                              <div className="flex flex-col items-center gap-2">
                                <UgcIcon className="border-neutral-white-dark size-10 overflow-visible rounded-full border bg-white p-2 drop-shadow-xs" />
                                <span className="text-neutral-grey-dark text-center text-2xl font-semibold">
                                  {t("ugcPlanTitle")}
                                </span>
                              </div>
                              <span className="text-neutral-black mt-3 text-center text-4xl font-bold md:text-5xl">
                                {t("ugcTitle")}
                              </span>
                            </div>

                            <div className="flex flex-col items-center justify-center gap-5">
                              <div className="flex flex-wrap justify-center gap-4 pt-10">
                                <div className="flex items-center gap-1">
                                  <CircleWithClockIcon className="size-5" />
                                  <span className="text-neutral-black font-semibold whitespace-nowrap">
                                    {formatPrice(UGC_COST_PER_VIDEO_15_SECONDS) +
                                      " " +
                                      t("perXSecondVideo", {second: 15})}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <CircleWithHalfClockIcon className="size-5" />
                                  <span className="text-neutral-black font-semibold whitespace-nowrap">
                                    {formatPrice(UGC_COST_PER_VIDEO_30_SECONDS) +
                                      " " +
                                      t("perXSecondVideo", {second: 30})}
                                  </span>
                                </div>
                              </div>
                              <div className="bg-neutral-grey-light h-px w-full" />
                              <span className="text-sm">{t("ugcDescription")}</span>
                              <Can I="create" a="Campaign">
                                <Button
                                  buttonStyle="Blue"
                                  onClick={() =>
                                    campaignActions.initNewCampaign({
                                      campaignType: "ugc",
                                    })
                                  }
                                  className="w-full gap-2">
                                  <span className="font-semibold text-white">{t("startUgcCampaign")}</span>
                                  <RightArrowIcon className="size-3.5 shrink-0 text-white" />
                                </Button>
                              </Can>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  </div>
                  <div className="flex flex-col">
                    {!isSwissBrand && (
                      <span className="text-neutral-grey-dark text-sm" ref={vatRef}>
                        {t("pricesDontIncludeVAT")}
                      </span>
                    )}
                    {isSwissBrand && (
                      <span className="text-neutral-grey-dark text-sm" ref={vatRef}>
                        {t("pricesDontIncludeVATSwitzerland", {vatPercentage: VAT_PERCENTAGE})}
                      </span>
                    )}
                    <span className="text-neutral-grey-dark text-sm" ref={serviceFeeRef}>
                      {t("pricesDontIncludeServiceFee", {
                        percentage: selectedCompany?.country === "United Kingdom" ? 15 : 7.5, // TODO: get fee percentage from backend
                      })}
                    </span>
                  </div>
                </div>
                {subscription?.name !== "STARTER" && subscription?.name !== "ADVANCED" && (
                  <iframe
                    ref={hubspotIframeRef}
                    className="h-175"
                    src={`https://meetings-eu1.hubspot.com/${!!subscription ? "luca-eberhardt/sales" : "luca-eberhardt/sales"}?embed=true&email=${encodeURIComponent(
                      user.email ?? "",
                    )}&firstName=${encodeURIComponent(user.first_name ?? "")}&lastName=${encodeURIComponent(
                      user.last_name ?? "",
                    )}&company=${encodeURIComponent(selectedCompany?.company_name ?? "")}`}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
